import { Box } from "@rebass/grid";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as tagHooks from "@rentiohq/shared-frontend/dist/reduxV2/tag/tag.hooks";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { ITag } from "@rentiohq/shared-frontend/dist/types/tag.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Card,
  ESpacings,
  Grid,
  Lozenge,
  Select,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import { FC, useState } from "react";
import { useDispatch } from "react-redux";

interface IProps {
  contact: IContact;
}

export const ContactTags: FC<IProps> = ({ contact }) => {
  const dispatch = useDispatch();
  const [selectedTagId, setSelectedTagId] = useState<number | undefined>(
    undefined,
  );
  const [isSaving, setIsSaving] = useState(false);

  const { items: allTags = [] } = tagHooks.usePaged({
    shouldRefetch: true,
    query: { page: 1, limit: 100 },
  });

  const currentTags: ITag[] = contact.tags || [];

  const tagOptions = allTags
    .filter(tag => !currentTags.some(ct => ct.id === tag.id))
    .map(tag => ({ value: tag.id, label: getLocalizedText(tag.key) }));

  const refreshContact = () => {
    dispatch(
      contactActions.getContact.actions.start({ contactId: contact.id }),
    );
  };

  const handleAdd = async () => {
    if (!selectedTagId) {
      return;
    }

    setIsSaving(true);
    try {
      await api.post(`/contacts/${contact.id}/tags`, {
        data: { tagId: selectedTagId },
        isV2: true,
      });
      setSelectedTagId(undefined);
      refreshContact();
    } catch {
      toast({
        heading: getLocalizedText("system.error"),
        variation: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (tagId: number) => {
    try {
      await api.delete(`/contacts/${contact.id}/tags/${tagId}`, {
        isV2: true,
      });
      refreshContact();
    } catch {
      toast({
        heading: getLocalizedText("system.error"),
        variation: "error",
      });
    }
  };

  return (
    <Card
      heading={getLocalizedText("system.entity.tags")}
      space={ESpacings.loose}
    >
      {currentTags.length > 0 && (
        <Box mb={2}>
          <Grid spacing="extraTight" alignItems="center">
            {currentTags.map(tag => (
              <Grid.Item key={tag.id}>
                <Grid spacing="extraTight" alignItems="center">
                  <Grid.Item>
                    <Lozenge appearance="info">
                      <Grid spacing="extraTight" alignItems="center">
                        <Grid.Item>{getLocalizedText(tag.key)}</Grid.Item>
                        <Grid.Item>
                          <Button
                            appearance="plain"
                            size="slim"
                            iconBeforeName="close"
                            color="blue"
                            onClick={() => handleRemove(tag.id)}
                          />
                        </Grid.Item>
                      </Grid>
                    </Lozenge>
                  </Grid.Item>
                </Grid>
              </Grid.Item>
            ))}
          </Grid>
        </Box>
      )}

      <Grid alignItems="flex-end" spacing="tight">
        <Grid.Item flex={1}>
          <Select
            id="contact-tag-select"
            label={getLocalizedText("tag.add.label")}
            options={tagOptions}
            value={selectedTagId}
            onChange={(option: { value: number; label: string } | null) =>
              setSelectedTagId(option?.value)
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Button
            appearance="primary"
            onClick={handleAdd}
            isDisabled={!selectedTagId || isSaving}
            isSubmitting={isSaving}
          >
            {getLocalizedText("system.save")}
          </Button>
        </Grid.Item>
      </Grid>
    </Card>
  );
};
